import { ApiError, parseApiError } from './apiError';

/**
 * The single fetch wrapper. Feature api clients call this; pages call the feature api
 * client, never `fetch` directly.
 *
 * Owns three cross-cutting concerns:
 *  - attaching the bearer token
 *  - transparently refreshing an expired access token, once, and replaying the request
 *  - normalizing every failure into an ApiError so callers never parse a raw Response
 */

const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? 'http://localhost:5048';

/**
 * Tokens live in memory, not localStorage. A token in localStorage is readable by any
 * script that gets injected into the page; keeping it in a module closure means an XSS has
 * to actively exfiltrate it during the session rather than simply reading it back later.
 * The refresh token is the one exception, below.
 */
let accessToken: string | null = null;

/**
 * The refresh token has to survive a page reload or the user is signed out on every
 * refresh. It is the weakest link in this design and it is a deliberate, bounded tradeoff:
 * the server rotates it on every use and revokes the entire chain on reuse, so a stolen
 * value is single-use and its theft is detectable.
 */
const REFRESH_STORAGE_KEY = 'rsys.refresh';

let refreshInFlight: Promise<string | null> | null = null;
let onAuthLost: (() => void) | null = null;

export function setAccessToken(token: string | null): void {
  accessToken = token;
}

export function getAccessToken(): string | null {
  return accessToken;
}

export function setRefreshToken(token: string | null): void {
  try {
    if (token) {
      localStorage.setItem(REFRESH_STORAGE_KEY, token);
    } else {
      localStorage.removeItem(REFRESH_STORAGE_KEY);
    }
  } catch {
    // Private browsing or blocked site data. The session still works; it just will not
    // survive a reload.
  }
}

export function getRefreshToken(): string | null {
  try {
    return localStorage.getItem(REFRESH_STORAGE_KEY);
  } catch {
    return null;
  }
}

/** Registered by AuthContext so a hard auth failure can clear app state and route to login. */
export function setAuthLostHandler(handler: (() => void) | null): void {
  onAuthLost = handler;
}

export function clearTokens(): void {
  accessToken = null;
  setRefreshToken(null);
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  signal?: AbortSignal;
  /** Set for the auth endpoints themselves, so a 401 there does not trigger a refresh loop. */
  skipRefresh?: boolean;
  /**
   * Extra request headers. Exists for ONE header today — `X-Reauthentication`, the one-time
   * proof a password-confirmed endpoint spends (`lib/reauth.ts`). It cannot override the
   * bearer token or the content type, which `send` sets after it.
   */
  headers?: Record<string, string>;
}

export async function api<T>(path: string, options: RequestOptions = {}): Promise<T> {
  return handle<T>(await request(path, options));
}

/**
 * Binary response variant, for downloading a file.
 *
 * Goes through the same {@link request} as everything else, so a download inherits the
 * bearer token, the one-shot refresh, and the normalized error. Reading the body as a Blob
 * is the only difference — a JSON parse would corrupt it.
 */
export async function apiBlob(
  path: string,
  options: RequestOptions = {},
): Promise<{ blob: Blob; fileName: string | null }> {
  const response = await request(path, options);

  if (!response.ok) {
    throw await parseApiError(response);
  }

  return {
    blob: await response.blob(),
    fileName: parseFileName(response.headers.get('content-disposition')),
  };
}

/**
 * An upload that REPORTS PROGRESS, over `XMLHttpRequest` rather than `fetch`.
 *
 * **The only reason this exists is the progress bar.** `fetch` has no upload-progress event —
 * a request-body stream would need HTTP/2 duplex and is not usable here — so a queue that
 * shows "34%" per file either uses XHR or shows a number it invented. A fake bar is worse
 * than none: it tells the reader a 30 MB file is nearly done when it has not left the tab.
 *
 * Everything else matches {@link api}: the bearer token, one transparent refresh and replay
 * on 401, and the same normalized {@link ApiError}. Reach for it ONLY where progress is
 * actually displayed — `api` stays the default for every other call.
 */
export async function apiUpload<T>(
  path: string,
  form: FormData,
  options: { onProgress?: (percent: number) => void; signal?: AbortSignal } = {},
): Promise<T> {
  const attempt = () =>
    new Promise<{ status: number; body: string }>((resolve, reject) => {
      const xhr = new XMLHttpRequest();

      xhr.open('POST', `${BASE_URL}${path}`);
      xhr.setRequestHeader('Accept', 'application/json');

      // Never set Content-Type for FormData: the browser adds the multipart boundary, and a
      // hand-set header would omit it and make the body unparseable.
      if (accessToken) {
        xhr.setRequestHeader('Authorization', `Bearer ${accessToken}`);
      }

      xhr.upload.addEventListener('progress', (event) => {
        if (!event.lengthComputable) return;

        /*
          Floored at 1 and capped at 99 while bytes are still moving. Zero reads as "nothing is
          happening" on a request that has started, and 100 before the server has answered
          reads as done on an upload that can still fail — the row goes to 100 when the
          response lands, not when the last byte leaves.
        */
        options.onProgress?.(
          Math.max(1, Math.min(99, Math.round((event.loaded / event.total) * 100))),
        );
      });

      xhr.addEventListener('load', () => resolve({ status: xhr.status, body: xhr.responseText }));
      xhr.addEventListener('error', () => reject(new ApiError('The upload could not be sent.', 0)));
      xhr.addEventListener('abort', () =>
        reject(new DOMException('The upload was cancelled.', 'AbortError')),
      );

      options.signal?.addEventListener('abort', () => xhr.abort(), { once: true });

      xhr.send(form);
    });

  let result = await attempt();

  if (result.status === 401) {
    const renewed = await refreshAccessToken();

    if (renewed) {
      result = await attempt();
    } else {
      clearTokens();
      onAuthLost?.();
    }
  }

  if (result.status < 200 || result.status >= 300) {
    /*
      Rebuilt into a Response so the ONE error parser handles it. A second hand-rolled reader
      here is how an upload's rejection ends up worded differently from every other call's —
      and `parseApiError` is what turns ValidationProblemDetails into a per-field message.
    */
    throw await parseApiError(
      new Response(result.body, {
        status: result.status,
        headers: { 'Content-Type': 'application/json' },
      }),
    );
  }

  return (result.body ? JSON.parse(result.body) : undefined) as T;
}

/**
 * One request, with a single transparent token refresh and replay on 401.
 */
async function request(path: string, options: RequestOptions): Promise<Response> {
  const { method = 'GET', body, signal, skipRefresh = false, headers } = options;

  const response = await send(path, method, body, signal, headers);

  if (response.status === 401 && !skipRefresh) {
    const renewed = await refreshAccessToken();

    if (renewed) {
      return send(path, method, body, signal, headers);
    }

    clearTokens();
    onAuthLost?.();
  }

  return response;
}

/**
 * Pulls the server's filename out of Content-Disposition, preferring the RFC 5987
 * `filename*` form so a non-ASCII name survives.
 */
function parseFileName(header: string | null): string | null {
  if (!header) {
    return null;
  }

  const encoded = /filename*=UTF-8''([^;]+)/i.exec(header);

  if (encoded) {
    try {
      return decodeURIComponent(encoded[1]);
    } catch {
      // A malformed header is not worth failing a download over.
    }
  }

  const plain = /filename="?([^";]+)"?/i.exec(header);

  return plain ? plain[1] : null;
}

async function send(
  path: string,
  method: string,
  body: unknown,
  signal?: AbortSignal,
  extra?: Record<string, string>,
): Promise<Response> {
  const isForm = body instanceof FormData;

  // The caller's extras FIRST, so nothing below — the content type, the bearer token — can be
  // overridden by a call site.
  const headers: Record<string, string> = { ...extra, Accept: 'application/json' };

  // Never set Content-Type for FormData: the browser has to add the multipart boundary,
  // and a hand-set header would omit it and make the body unparseable.
  if (body !== undefined && !isForm) {
    headers['Content-Type'] = 'application/json';
  }

  if (accessToken) {
    headers.Authorization = `Bearer ${accessToken}`;
  }

  return fetch(`${BASE_URL}${path}`, {
    method,
    headers,
    body: body === undefined ? undefined : isForm ? (body as FormData) : JSON.stringify(body),
    signal,
  });
}

/**
 * Refreshes the access token. Concurrent callers share one in-flight request so a burst of
 * 401s does not fire a burst of refreshes — which the server's reuse detection would
 * correctly treat as token theft and revoke the whole chain.
 */
async function refreshAccessToken(): Promise<string | null> {
  if (refreshInFlight) {
    return refreshInFlight;
  }

  const stored = getRefreshToken();

  if (!stored) {
    return null;
  }

  refreshInFlight = (async () => {
    try {
      const response = await fetch(`${BASE_URL}/api/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ refreshToken: stored }),
      });

      if (!response.ok) {
        return null;
      }

      const payload = (await response.json()) as { accessToken: string; refreshToken: string };

      setAccessToken(payload.accessToken);
      setRefreshToken(payload.refreshToken);

      return payload.accessToken;
    } catch {
      return null;
    } finally {
      refreshInFlight = null;
    }
  })();

  return refreshInFlight;
}

async function handle<T>(response: Response): Promise<T> {
  if (response.status === 204) {
    return undefined as T;
  }

  if (!response.ok) {
    throw await parseApiError(response);
  }

  const text = await response.text();
  return (text ? JSON.parse(text) : undefined) as T;
}

export { ApiError };
